import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowUp, ArrowRight } from "lucide-react";
import content from "@/content/guerisseurIntergalactique.json";

const reveal = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.6 },
};

export const GuerisseurIntro = () => (
    <section className="mb-14" data-testid="guerisseur-intro">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3 space-y-4 text-lg text-foreground/80 leading-relaxed">
                {content.intro.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <motion.figure {...reveal} className="lg:col-span-2 rounded-3xl overflow-hidden shadow-xl">
                <img
                    src="/images/soins/portail-cosmique-guerisseur-intergalactique.webp"
                    alt="Portail cosmique de lumière ouvert pendant un soin multidimensionnel"
                    className="w-full h-full object-cover aspect-[4/3]"
                    data-testid="guerisseur-intro-image"
                />
            </motion.figure>
        </div>
        <motion.aside
            {...reveal}
            className="mt-10 rounded-3xl border border-secondary/40 bg-secondary/5 p-8 sm:p-10 relative overflow-hidden"
            data-testid="guerisseur-definition"
        >
            <Sparkles className="absolute -right-4 -top-4 w-28 h-28 text-secondary/10" />
            <div className="text-xs tracking-[0.25em] uppercase text-secondary mb-3 font-medium">{content.definition.eyebrow}</div>
            <h2 className="font-serif text-2xl sm:text-3xl mb-5">{content.definition.title}</h2>
            <div className="space-y-4 text-foreground/80 leading-relaxed">
                {content.definition.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
        </motion.aside>
    </section>
);

const Section = ({ s, flip }) => (
    <motion.section {...reveal} id={s.id} className="py-10 border-t border-border/50" data-testid={`guerisseur-section-${s.id}`}>
        <div className={s.image ? "grid lg:grid-cols-5 gap-10 items-center" : "max-w-3xl"}>
            <div className={s.image ? `lg:col-span-3 ${flip ? "lg:order-2" : ""}` : ""}>
                <div className="text-xs tracking-[0.25em] uppercase text-primary mb-3 font-medium">{s.eyebrow}</div>
                <h2 className="font-serif text-3xl sm:text-4xl mb-6">{s.title}</h2>
                <div className="space-y-4 text-foreground/80 leading-relaxed">
                    {s.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                </div>
            </div>
            {s.image && (
                <figure className="lg:col-span-2 rounded-3xl overflow-hidden shadow-lg">
                    <img src={s.image} alt={s.image_alt} loading="lazy" className="w-full object-cover aspect-[3/4] max-h-[520px]" />
                </figure>
            )}
        </div>
    </motion.section>
);

export const GuerisseurSections = () => (
    <div className="mt-16" data-testid="guerisseur-sections">
        {content.sections.map((s, i) => <Section key={s.id} s={s} flip={i % 4 === 2} />)}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-4">
            <a
                href="#choisir-soin"
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 h-12 hover:scale-[1.02] transition-transform"
                data-testid="guerisseur-cta-choose"
            >
                <ArrowUp className="w-4 h-4" /> Choisir mon soin de lumière
            </a>
            <a
                href="/journal/soin-guerisseur-intergalactique"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline underline-offset-4"
                data-testid="guerisseur-article-link"
            >
                Lire l'article : ce qui se vit pendant un soin multidimensionnel <ArrowRight className="w-3.5 h-3.5" />
            </a>
        </div>
    </div>
);
